import React, { useMemo, useState, useEffect } from 'react';
import { Stack, Tooltip, Text, useMantineColorScheme } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CollectorType, ContextMessage } from '@/client/features/signals/types';
import { fetchThreadGET } from '../services/threadApi';
import { ThreadActions } from './ThreadActions';
import { LoadingState } from './LoadingState';
import { MessageList } from '@/client/features/signals/components/MessageList';

type ShowContextProps = {
  signalId: string;
  threadPath: string;
  collector: string;
  signalPublishedAt?: string;
};

export const PostContextHistory = ({
  signalId,
  threadPath,
  collector,
  signalPublishedAt,
}: ShowContextProps) => {
  const qc = useQueryClient();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const [expanded, setExpanded] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);
  const [messages, setMessages] = useState<ContextMessage[]>([]);
  const [, setOriginalContext] = useState<ContextMessage[]>([]);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [loadingNext, setLoadingNext] = useState(false);

  const filterKey =
    collector === CollectorType.Telegram
      ? `thread_telegram:"${threadPath}"`
      : `thread_forum:"${threadPath}"`;

  const { data: firstMessageList, isFetching: firstLoading } = useQuery({
    queryKey: ['thread:first', signalId, threadPath, collector],
    queryFn: async () => {
      if (collector === CollectorType.Telegram && signalPublishedAt) {
        const prevAndCurrent = await fetchThreadGET(signalId, {
          length: '6',
          order: '-created_at',
          filter: [`created_at<="${signalPublishedAt}" AND ${filterKey}`],
        });
        const next5 = await fetchThreadGET(signalId, {
          length: '5',
          order: 'created_at',
          filter: [`created_at>="${signalPublishedAt}" AND ${filterKey}`],
        });
        return [...prevAndCurrent, ...next5].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
      }
      return fetchThreadGET(signalId, { length: '1', order: 'created_at', filter: [filterKey] });
    },
    enabled: collector !== CollectorType.Telegram,
  });

  const listToRender = messages.length ? messages : (firstMessageList ?? []);

  const expandToFive = async () => {
    const five = await qc.fetchQuery({
      queryKey: ['thread:first5', signalId, threadPath, collector, signalPublishedAt],
      queryFn: async () => {
        if (collector === CollectorType.Telegram && signalPublishedAt) {
          const prevAndCurrent = await fetchThreadGET(signalId, {
            length: '6',
            order: '-created_at',
            filter: [`created_at<="${signalPublishedAt}" AND ${filterKey}`],
          });
          const next5 = await fetchThreadGET(signalId, {
            length: '5',
            order: 'created_at',
            filter: [`created_at>="${signalPublishedAt}" AND ${filterKey}`],
          });
          return [...prevAndCurrent, ...next5].sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
          );
        }
        return fetchThreadGET(signalId, { length: '5', order: 'created_at', filter: [filterKey] });
      },
    });
    setMessages(five);
    setExpanded(true);
    setAllLoaded(false);
    if (collector === CollectorType.Telegram) setOriginalContext(five);
  };

  const loadAll = async () => {
    const all = await qc.fetchQuery({
      queryKey: ['thread:all', signalId, threadPath],
      queryFn: () => fetchThreadGET(signalId, { order: 'created_at', filter: [filterKey] }),
    });
    setMessages(all);
    setAllLoaded(true);
  };

  const loadPrev25 = async () => {
    if (loadingPrev || !listToRender.length) return;
    setLoadingPrev(true);

    try {
      const oldestDate = listToRender[listToRender.length - 1]?.date;
      if (!oldestDate) return;

      const prev = await qc.fetchQuery({
        queryKey: ['thread:prev25', signalId, threadPath, oldestDate, Date.now()],
        queryFn: () =>
          fetchThreadGET(signalId, {
            length: '25',
            order: '-created_at',
            filter: [`created_at<="${oldestDate}" AND ${filterKey}`],
          }),
        staleTime: 0,
      });

      if (!prev?.length) return;

      const prevDesc = [...prev].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );

      setMessages(prevDesc);
    } finally {
      setLoadingPrev(false);
    }
  };

  const loadNext25 = async () => {
    if (loadingNext || !listToRender.length) return;
    setLoadingNext(true);

    try {
      const newestDate = listToRender[0]?.date;
      if (!newestDate) return;

      const next = await qc.fetchQuery({
        queryKey: ['thread:next25', signalId, threadPath, newestDate, Date.now()],
        queryFn: () =>
          fetchThreadGET(signalId, {
            length: '25',
            order: 'created_at',
            filter: [`created_at>="${newestDate}" AND ${filterKey}`],
          }),
        staleTime: 0,
      });

      if (!next?.length) return;

      const nextDesc = [...next].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );

      setMessages(nextDesc);
    } finally {
      setLoadingNext(false);
    }
  };

  const backToOriginalMention = async () => {
    setLoadingPrev(false);
    setLoadingNext(false);
    setAllLoaded(false);

    if (collector === CollectorType.Telegram && signalPublishedAt) {
      const prevAndCurrent = await fetchThreadGET(signalId, {
        length: '6',
        order: '-created_at',
        filter: [`created_at<="${signalPublishedAt}" AND ${filterKey}`],
      });

      const next5 = await fetchThreadGET(signalId, {
        length: '5',
        order: 'created_at',
        filter: [`created_at>="${signalPublishedAt}" AND ${filterKey}`],
      });

      const combined = [...prevAndCurrent, ...next5].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );

      setMessages(combined);
      return;
    }

    const first = await fetchThreadGET(signalId, {
      length: '1',
      order: 'created_at',
      filter: [filterKey],
    });

    setMessages(first);
  };

  useEffect(() => {
    if (collector === CollectorType.Telegram && firstMessageList?.length)
      setOriginalContext(firstMessageList);
  }, [collector, firstMessageList]);

  const sides = useMemo(() => {
    const result: ('left' | 'right')[] = [];
    let lastSide: 'left' | 'right' = 'left';
    let lastAuthor: string | undefined;
    listToRender.forEach((m, i) => {
      const side =
        i === 0
          ? 'left'
          : m.author === lastAuthor
            ? lastSide
            : lastSide === 'left'
              ? 'right'
              : 'left';
      result.push(side);
      lastSide = side;
      lastAuthor = m.author;
    });
    return result;
  }, [listToRender]);

  return (
    <Stack gap="xs">
      <LoadingState firstLoading={firstLoading} listToRender={listToRender} />
      {expanded && (
        <MessageList list={listToRender} sides={sides} isDark={isDark} collector={collector} />
      )}
      {!expanded ? (
        <Tooltip label="Open Thread" withArrow>
          <Text
            size="sm"
            style={{
              cursor: 'pointer',
              fontWeight: 'bold',
              width: 'fit-content',
              display: 'inline-block',
              alignSelf: 'flex-start',
            }}
            onClick={expandToFive}
          >
            […]
          </Text>
        </Tooltip>
      ) : collector === CollectorType.Telegram ? (
        <ThreadActions
          onLoadPrev={loadPrev25}
          onLoadNext={loadNext25}
          onBack={backToOriginalMention}
        />
      ) : null}
      {!allLoaded && expanded && collector !== CollectorType.Telegram && (
        <Text
          size="sm"
          style={{
            textDecoration: 'underline',
            cursor: 'pointer',
            width: 'fit-content',
            color: 'var(--quo-blue-80)',
          }}
          onClick={loadAll}
        >
          Load all the thread…
        </Text>
      )}
    </Stack>
  );
};
